import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI } from '../api/products';
import ProductCard from '../components/common/ProductCard';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const data = await productsAPI.getAll({ limit: 8 });
      setFeaturedProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      alert(`Subscribed with ${email}`);
      setEmail('');
    }
  };

  return (
    <div>
      {/* Hero Section - Minimal Black/White */}
      <section className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-wider text-gray-400 mb-3 block">
              Summer 2024
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-5 tracking-tight">
              Minimal. Modern. You.
            </h1>
            <p className="text-gray-300 text-base md:text-lg mb-8 leading-relaxed">
              Discover our new collection — thoughtfully designed, sustainably made.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/products" 
                className="inline-flex items-center justify-center bg-white text-gray-900 px-8 py-3 text-sm font-medium hover:bg-gray-100 transition"
              >
                Shop Collection
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </Link>
              <Link 
                to="/about" 
                className="inline-flex items-center justify-center border border-gray-700 text-white px-8 py-3 text-sm font-medium hover:bg-gray-800 transition"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section - Clean Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-light tracking-wide uppercase text-gray-900">
              Shop by Category
            </h2>
            <div className="w-12 h-px bg-gray-300 mx-auto mt-4"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Men', 'Women', 'Kids', 'Accessories'].map((category) => (
              <Link
                key={category}
                to={`/products?category=${category.toLowerCase()}`}
                className="group block"
              >
                <div className="bg-gray-50 h-48 flex items-center justify-center border border-gray-100 group-hover:border-gray-300 transition">
                  <span className="text-gray-600 text-base font-light tracking-wide group-hover:text-gray-900 transition">
                    {category}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products - Minimal Layout */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="text-2xl font-light tracking-wide uppercase text-gray-900">
                Featured
              </h2>
              <div className="w-12 h-px bg-gray-300 mt-3"></div>
            </div>
            <Link to="/products" className="text-xs text-gray-400 hover:text-gray-900 uppercase tracking-wider transition">
              View All →
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 h-80"></div>
                  <div className="mt-4 space-y-2">
                    <div className="bg-gray-200 h-3 w-3/4"></div>
                    <div className="bg-gray-200 h-3 w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Editorial Banner */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs uppercase tracking-wider text-gray-400">Sustainability</span>
              <h3 className="text-3xl font-light mt-3 mb-5 tracking-tight">Made with purpose</h3>
              <p className="text-gray-500 leading-relaxed mb-6">
                Every piece is crafted using eco-friendly materials and ethical production practices. 
                Because style shouldn't come at the cost of our planet.
              </p>
              <Link to="/about" className="text-sm text-gray-900 border-b border-gray-300 hover:border-gray-900 transition pb-0.5">
                Our Story
              </Link>
            </div>
            <div className=" h-80 flex items-center justify-center">
              <img width={500} src="https://theperfectstyle.co.uk/cdn/shop/files/IMG_1613.jpg?v=1772012066&width=1500" alt="" />
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section - Minimal */}
      <section className="py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-light tracking-wide uppercase mb-3">Newsletter</h2>
            <div className="w-12 h-px bg-gray-300 mx-auto mb-6"></div>
            <p className="text-gray-500 text-sm mb-8">
              Subscribe for new arrivals, lookbooks, and exclusive offers.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-4 py-3 text-sm border border-gray-200 focus:outline-none focus:border-gray-400 transition bg-white"
                required
              />
              <button 
                type="submit" 
                className="bg-gray-900 text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;